import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';
import {useAppDispatch} from './Store';
import BookingCreatorContainer from "app/booking-creator/container";
import ToastDialog from "external-components/toast-dialog";
import ModalContainer from "design-v2/common/ModalContainer";
import GenericError from "design-v2/common/GenericError";
import {DiaryRootState} from "appRoot/rootReducer";
import {rootDisplayActions} from "app/reducers/display/slice";
import {errorStateActions} from "app/reducers/errorState/slice";
import {floorplanBookingsActions} from "floorplan-v2/app/reducers/bookings/slice";
import { bookingCreatorSettingActions } from 'booking-creator/reducers/setting/slice';
import LoginActions from "app/actions/login.actions";
import {
  bookingActions,
  syncCouchBookingUpdates,
  updateDelayedBookings
} from 'internal-reducers/booking/slice';
import StaffNamePanelContainer from 'booking-card-list/StaffNamePanelContainer';
import {
  saveQuickEditBooking,
  quickValidationActions,
  updateCanSaveStatusChange
} from 'internal-reducers/quickValidation/slice';
import {isEmpty, isEqual} from 'lodash-es';

import AdminService from "external-services/diary-admin-service/admin.service";
import {venueActions} from "app/reducers/venue/slice";
import {settingActions} from "app/reducers/settings/slice";
import {
  DiaryMobileVersion,
  IBooking,
  IDiaryVenue,
  BookingMethods,
  loadStatus,
  IBookingStatus,
} from "shared-types";
import {PouchLoadStatus} from "app/actions/pouch.actions";
import { blockoutPouchListener, loadVenueNotes, setSelectedSchedule } from "app/reducers/schedule/slice";
import {loadBookingsFromCurrentSchedule} from "app/reducers/booking/slice";

import GeneralSettingsService from 'external-services/general-settings-service/generalSettings.service';
import BookingCardListLazyLoader from 'app/booking-card-list/LazyLoader';
import {handlePouchUpdates} from 'appRoot/pouchReduxUpdates';
import FloatingBookingCard from 'app/floorplan-v2/FloatingBookingCard/container';
import StatusSelectorModal from 'design-v2/common/StatusSelectorModal'
import { StatusServiceInstance } from 'external-services/status-service';
import { floorplanSettingsActions } from 'floorplan-v2/app/reducers/settings/slice';
import { staffValidationActions } from 'app/reducers/staffValidation/slice';
import { pick } from 'lodash-es';
import DraggableBookingCard from "app/floorplan-v2/DraggableBookingCard";
import { PreAuthDialog } from 'booking-creator/components/ManagePreAuth/preAuthDialog';
import {BookingCreatorState} from "booking-creator/rootReducer";
import { paymentActions } from 'booking-creator/reducers/payment/slice';
import { resetReducers } from 'booking-creator/hooks';
import {MainViewType} from "app/services/mainViewVisibility/types";
import {appRoutes} from "appRoot/RouteManager";
import {UserPointerType} from "external-services/pointer-detection-service/types";
import PointerDetectionService from "external-services/pointer-detection-service";
import { MessageService } from 'external-services/message-service';
import {appValues} from "appRoot/app.constants";
import SwCacheService from "app/services/swCache.service";

const NS = 'DiaryBookings';

const forceBookingCardList = !!window.config?.forceBookingCardList;
const forceBookingCreator = !!window.config?.forceBookingCreator;

const venueDataReady = () => {
  return new Promise<IDiaryVenue>((resolve) => {
    LoginActions.registerForReadyCB(() => {
      const venue = AdminService.getVenue();
      resolve(venue);
    });
  });
}

const BookingCreatorContainerFinalRender = () => {
  return  <BookingCreatorContainer />
};

const BookingCreatorMemo = memo(BookingCreatorContainerFinalRender, () => {
  return true;
});

const BookingCardListMemo = React.memo(BookingCardListLazyLoader);

console.log(`
------------------------------------------------------

--- APP VERSION - Short hash: ${appValues.VERSION.split("|").join(" Built: ")}

------------------------------------------------------
`);

export default function BookingsRoot() {

  const dispatch = useAppDispatch();
  const enableNewFloorPlan = useSelector((state: DiaryRootState) => state.venueReducer.enableNewFloorPlan);
  const pouchLoadedStatus = useSelector((state: DiaryRootState) => state.pouchReducer.pouchLoadedStatus);
  const selectedDate = useSelector((state: DiaryRootState) => state.scheduleReducer.selectedDate);
  const toastPayload = useSelector((state: DiaryRootState) => state.rootDisplayReducer.toastPayload);
  const appLoadState = useSelector((state: DiaryRootState) => state.settingReducer.appLoadState);
  const enableBookingCreatorOnFloorplan = useSelector((state: DiaryRootState) => state.floorplanSettingsReducer.floorplanSettings.enableBookingCreatorOnFloorplan);
  const enableBookingCreatorOnTimeline = useSelector((state: DiaryRootState) => state.floorplanSettingsReducer.floorplanSettings.enableBookingCreatorOnTimeline);
  const useBookingCardList = useSelector((state: DiaryRootState) => state.floorplanSettingsReducer.floorplanSettings.useBookingCardList);
  const routeStatus = useSelector((state: DiaryRootState) => state.rootDisplayReducer.routeStatus);
  const {
    errorTitle,
    errorMessage,
    accordionDetail,
    errorSeverity,
    accordionTitle,
    isErrorShowing,
    isOffline,
    extraButton,
  } = useSelector((state: DiaryRootState) => state.errorStateReducer);
  const selectedBookingForStatusModal = useSelector((state: DiaryRootState) => state.floorplanBookingsReducer?.selectedBookingForStatusModal);
  const couchReducedBookings = useSelector((state: DiaryRootState) => state.bookingReducer.couchReducedBookings);
  const fullStatusOptions = useSelector((state: DiaryRootState) => state.statusReducer.fullStatusOptions);
  const canSave = useSelector((state: DiaryRootState) => state.quickValidationReducer.canSave);
  const cantSaveMessageList = useSelector((state: DiaryRootState) => state.quickValidationReducer.cantSaveMessageList, isEqual);
  const staffEnabled = useSelector((state: DiaryRootState) => state.settingReducer.generalSettings?.staffEditEnabled);
  const newStatus = useSelector((state: DiaryRootState) => state.quickValidationReducer.newStatus);
  const originStatus = useSelector((state: DiaryRootState) => state.quickValidationReducer.originStatus);
  const staffEditEnabled = useSelector((state: DiaryRootState) => state.staffValidationReducer.staffEditEnabled);
  const newPax = useSelector((state: DiaryRootState) => state.quickValidationReducer?.newPax);
  const originPax = useSelector((state: DiaryRootState) => state.quickValidationReducer?.originPax);
  const staffValue = useSelector((state: DiaryRootState) => state.quickValidationReducer?.staffValue);
  const bookingView = useSelector((state: DiaryRootState) => state.settingReducer.bookingView);
  // prettier-ignore
  const preAuthDialog = useSelector((state: BookingCreatorState) => state.paymentReducer.preAuthDialog);
  // prettier-ignore
  const isSetActiveBookingInBookingCreator = useSelector((state: BookingCreatorState) => state.paymentReducer.isSetActiveBookingInBookingCreator);

  const appFullyLoaded = appLoadState === loadStatus.loaded;

  const enableBookingCreator = forceBookingCreator || (enableNewFloorPlan && (enableBookingCreatorOnFloorplan || enableBookingCreatorOnTimeline));

  const isRenderStaffPanel = useMemo(() => {
    return (Boolean(newStatus) && Boolean(originStatus) && newStatus.name !== originStatus.name)
  }, [newStatus, originStatus]);

  useEffect(() => {
    dispatch(updateCanSaveStatusChange());
  }, [staffValue, newPax, originPax, originStatus, newStatus]);

  const CommonStaffNamePanelRender = (): JSX.Element => {
    return <StaffNamePanelContainer isDisplay={isRenderStaffPanel} />;
  };

  const [isSettingsReady, setIsSettingsReady] = useState(false);

  const errorButtonConfig = useMemo(() => {
    if (!extraButton) {
      return {
        text: 'Close',
        cb: () => dispatch(errorStateActions.hideErrorState()),
      };
    }

    return {
      text: extraButton.text,
      cb: extraButton.cb,
    };
  }, [extraButton]);

  useEffect(() => {
    dispatch(syncCouchBookingUpdates());
    SwCacheService.unregisterWhenSkipped();
  }, []);

  useEffect(() => {
    dispatch(bookingActions.clearRecentChangeBookingIds());
  }, [couchReducedBookings]);

  useEffect(() => {
    PointerDetectionService.detectPointerType((pointerType: UserPointerType) => {
      dispatch(settingActions.setCurrentPointerDevice(pointerType));
    });
  }, []);

  useEffect(() => {
    if (selectedBookingForStatusModal) {
      const selectedBooking = couchReducedBookings.find(({ _id }) => _id === selectedBookingForStatusModal.bookingId)
      const selectedStatus = fullStatusOptions.find((status) => status._id === selectedBookingForStatusModal.statusId)
      if (selectedStatus) {
        dispatch(
          quickValidationActions.clearValidationAndSetBothStatuses({
            status: selectedStatus,
            isIgnoreStaff: selectedBooking.method === BookingMethods.walkin
          })
        );
      }
    }

  }, [selectedBookingForStatusModal?.statusId, selectedBookingForStatusModal?.bookingId, fullStatusOptions, couchReducedBookings])

  const handleQuickSelectStatus = (status: IBookingStatus) => {
    dispatch(quickValidationActions.setNewStatus(status))
  }

  useEffect(() => {
    // if (doNothing) {
    //   return;
    // }

    if(appFullyLoaded) {
      dispatch(bookingCreatorSettingActions.setWideVersion(true));

      const setVenue = async () => {
        const venue = await venueDataReady();
        if (!isEmpty(venue.titleSubscription)) {
          venue.titleSubscription = MessageService.getMessage(
            venue.titleSubscription,
            { venueName: venue.name },
            null
          )
        }
        dispatch(venueActions.setVenueDetails(venue));
        dispatch(settingActions.setMobileVersion(DiaryMobileVersion.PlusVersion)); // @todo: have option for Diary

        /**
         * @todo
         * Need to check implement below code
         */
        // const { success, reason } = await LoginService.onLogin(
        //   dispatch,
        //   venue,
        //   account,
        //   isLegacyLogin
        // );

        // if (!success) {
        //   dispatch(
        //     errorStateActions.setErrorState({
        //       errorTitle: 'Problem logging in',
        //       errorMessage: `There was a problem with one of the login processes. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
        //       accordionDetail: getAccordionDetail({ message: reason }),
        //     })
        //   );
        //   handleVenueLoading(false);
        //   return;
        // }

        GeneralSettingsService.get().then((settings) => {
          dispatch(settingActions.setGeneralSettings(settings));
          dispatch(settingActions.setMaxBookingSize(settings.restrictedMaxBookingSize));
          // for ploorplan-v2
          if (settings.floorPlanSettings) {
            dispatch(floorplanSettingsActions.setFloorplanSettings(settings.floorPlanSettings));
          } else {
            dispatch(floorplanSettingsActions.resetFloorplanSettings());// reset to initial state for venue enabled new floorplan but never saved setting
          }
          dispatch(
            staffValidationActions.setStaffInitialSettings(
              pick(settings, [
                'staffEditEnabled',
                'staffEditRequired',
                'staffEnabled',
                'staffRequired',
                'staffNew',
                'staffEdit'
              ])
            )
          );
          setIsSettingsReady(true);
        });

        // dispatch(settingActions.setAppVersion(appValues.VERSION));
        // dispatch(settingActions.setIsManager(ManagerService.isManager));

        // dispatch(venueActions.setIsSwitchVenue(false));
      }

      setVenue();
      handlePouchUpdates(dispatch)
    }

  }, [appLoadState]);
  useEffect(() => {
    const blockoutListener = blockoutPouchListener(dispatch);
    return () => {
      blockoutListener();
    }
  }, []);
  useEffect(() => {

    const loadBookingsFromSchedule = async () => {
      await dispatch(setSelectedSchedule({ useCachedScheduleLookup: true }));
      await dispatch(loadVenueNotes(selectedDate));
      await dispatch(loadBookingsFromCurrentSchedule()); // needed for status modal in runsheet
    };

    if (pouchLoadedStatus === PouchLoadStatus.success && selectedDate) {
      loadBookingsFromSchedule();
    }
  }, [pouchLoadedStatus, selectedDate.getTime()]);

  const bookingStatusList = useMemo(() => {
    if (selectedBookingForStatusModal) {
      const selectedBooking = couchReducedBookings.find(({ _id }) => _id === selectedBookingForStatusModal.bookingId)
      return StatusServiceInstance.getFilteredStatusList(
        selectedBooking.status?.name as any,
        selectedBooking as IBooking
      );
    }
    return []
  }, [selectedBookingForStatusModal]);
  // this is handled in BookingCreatorSlide.tsx
  // const handleCreateBooking = (bookingMethod: BookingMethods) => {};


  const handleStatusChange = async (statusId: string) => {
    const status = fullStatusOptions.find((option) => option._id === statusId);
    const selectedBooking = couchReducedBookings.find(({ _id }) => _id === selectedBookingForStatusModal.bookingId)
    if (selectedBooking.status._id !== statusId && status?.name) {

      await dispatch(
        updateDelayedBookings({
          delayedBooking: selectedBooking,
          changedProperty: 'status',
        })
      );
      await dispatch(
        saveQuickEditBooking({
          name: status.name,
          isManualUpdate: true,
          booking: selectedBooking,
        })
      );
    }
    
    // // Add delay for testing the loader
    // await new Promise(resolve => setTimeout(resolve, 2000));
    
    dispatch(floorplanBookingsActions.setSelectedBookingForStatusModal())
  };

  const onOpenCloseDialog = useCallback(() => {
    dispatch(paymentActions.setPreAuthDialog(!preAuthDialog));
    if (!isSetActiveBookingInBookingCreator) {
      resetReducers(dispatch);
    }
    setTimeout(() => {
      dispatch(paymentActions.resetPreAuthState());
    });
  }, [preAuthDialog, isSetActiveBookingInBookingCreator]);

  const selectedBooking = useMemo(() => {
    if (selectedBookingForStatusModal) {
      return couchReducedBookings.find(({ _id }) => _id === selectedBookingForStatusModal.bookingId)
    }
    return null
  }, [selectedBookingForStatusModal, couchReducedBookings])

  const shownBCL = useMemo(() => {
    if (!enableNewFloorPlan && !forceBookingCardList) {
      return false
    }
    if (isSettingsReady && routeStatus === appRoutes.bookings) {
      if (useBookingCardList || bookingView === MainViewType.FloorplanV2) {
        return true
      }
    }
    return false
  }, [bookingView, enableNewFloorPlan, isSettingsReady, routeStatus, useBookingCardList])
  return (
    <div>
      {selectedBookingForStatusModal ? (
        <StatusSelectorModal
          canSave={canSave}
          cantSaveMessageList={cantSaveMessageList}
          closeDialog={() => dispatch(floorplanBookingsActions.setSelectedBookingForStatusModal())}
          statusId={selectedBookingForStatusModal.statusId}
          bookingStatusList={bookingStatusList}
          handleStatusChange={handleStatusChange}
          handleQuickSelectStatus={handleQuickSelectStatus}
          staffRequired={staffEnabled}
          StaffPanel={CommonStaffNamePanelRender}
          paymentDetails={selectedBooking?.paymentDetails}
          bookingType={selectedBooking?.bookingType}
          staffEditEnabled={staffEditEnabled}
        />
      ) : null}
      {enableBookingCreator && isSettingsReady && <BookingCreatorMemo />}

      {shownBCL && (
        <BookingCardListMemo />
      )}

      {enableNewFloorPlan && (
        <>
          <FloatingBookingCard />
          <DraggableBookingCard />


          <ToastDialog
            msg={toastPayload.isCleanStyle ? '' : toastPayload.msg}
            type={toastPayload.type}
            isImportant={toastPayload.isImportant}
            handleClose={() => dispatch(rootDisplayActions.clearToast())}
            customButton1Text={toastPayload.customButton1Text}
            handleCustomButton1={toastPayload.handleCustomButton1}
            customButton2Text={toastPayload.customButton2Text}
            handleCustomButton2={toastPayload.handleCustomButton2}
            customButton3Text={toastPayload.customButton3Text}
            handleCustomButton3={toastPayload.handleCustomButton3}
          />

          <ModalContainer
            isOpen={isErrorShowing}
            onRequestClose={() => {
              dispatch(errorStateActions.hideErrorState());
            }}
            isMaxWidth={true}
            contentStyle={{
              maxWidth: 800,
            }}
          >
            <GenericError
              errorDetail={errorMessage}
              errorTitle={errorTitle}
              accordionTitle={accordionTitle}
              accordionDetail={accordionDetail}
              errorSeverity={errorSeverity}
              extraButtonText={errorButtonConfig.text}
              extraButtonClickHandler={errorButtonConfig.cb}
            />
          </ModalContainer>
          <ModalContainer
            isOpen={preAuthDialog}
            onRequestClose={onOpenCloseDialog}
            isMaxWidth={true}
            contentStyle={{
              maxWidth: 600,
            }}
          >
            <PreAuthDialog onOpenCloseDialog={onOpenCloseDialog} />
          </ModalContainer>
        </>
      )}
    </div>
  );
}

